/**
 * `/opportunities/new` — **the export-control attestation comes first** (§8).
 *
 * One question, before any other field, in wording that must not be
 * paraphrased. Answering "Yes, or I'm not sure" stops the form and **no record
 * is created**: the answer is still sent, because both answers are recorded
 * server-side and the client cannot skip the question — a client-side check is
 * theatre, one API call around it and the record exists.
 *
 * §8 item 6's pre-selection sits awkwardly with "the question comes first",
 * and this is how it is resolved: the question is asked first with no default,
 * and if the account or program the user then picks is `export_sensitive`, the
 * answer is **reset to "Yes"** and the question is asked again with the reason
 * shown. A rep must actively override rather than click through, which is the
 * point of item 6, and the question still precedes every other field.
 *
 * After the attestation the form asks for the minimum to create a `PROSPECT`:
 * name, account, owner, price basis, program life (3 years unless changed), and
 * one product line. **Price basis is
 * asked at creation** — it is an opportunity-level fact (§5.3) that decides
 * what every value on the screen means, and it defaults from the account's
 * `channel_type`.
 *
 * **`New account` sits beside the Account field**, because the create row
 * inside the dropdown only appears once something has been typed — the one
 * state a rep who has never heard of this customer is not in. It opens the
 * same `CreateAccountDialog` the picker does: one form, one answer to what a
 * valid account is, and §6.8's warn-never-block duplicate check comes with it.
 * Ungated like `New opportunity` beside it — `TIERS` has no read-only tier and
 * the server enforces (§2 rule 1). The dialog renders outside the `<form>`:
 * its buttons are plain `<button>`s, and inside the form they would submit it.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import {
  DEFAULT_PROGRAM_YEARS,
  PRICE_BASES,
  PROGRAM_LIFE_OPTIONS,
  REGIONS,
  SEGMENTS,
} from '@menlo/shared';
import { api, ApiError } from '@/lib/api';
import { useApiQuery } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { useMe } from '@/auth/useMe';
import { useReferenceData } from '@/lib/reference';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { ExportAttestation, ExportRefusal } from '@/features/export/ExportAttestation';
import { AccountPicker, CreateAccountDialog } from '@/features/pickers/AccountPicker';
import { humanize } from '@/lib/format';

interface AccountRecord {
  id: string;
  name: string;
  region: string;
  segment: string;
  channel_type: string;
  rep_firm_id: string | null;
  export_sensitive: boolean;
}

export function NewOpportunity(): React.ReactElement {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const reference = useReferenceData();
  const { push } = useToast();

  const [attestation, setAttestation] = useState<boolean | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  /** Open state of the `New account` dialog, and what it created if it did. */
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [createdAccount, setCreatedAccount] = useState<{ id: string; name: string } | null>(null);
  const [ownerId, setOwnerId] = useState<string>('');
  const [priceBasis, setPriceBasis] = useState<string>('menlo_net');
  const [region, setRegion] = useState<string>('AMER');
  const [segment, setSegment] = useState<string>('other');
  const [programYears, setProgramYears] = useState<number>(DEFAULT_PROGRAM_YEARS);
  const [productId, setProductId] = useState<string>('');
  const [units, setUnits] = useState<string>('');
  const [asp, setAsp] = useState<string>('');

  const account = useApiQuery<{ account: AccountRecord }>(
    ['account', accountId ?? ''],
    accountId === null ? null : `/api/accounts/${accountId}`,
  );

  const isManager = me?.tier === 'Manager' || me?.tier === 'Admin';
  const sensitiveAccount =
    accountId !== null && reference.export_sensitive_accounts.includes(accountId);

  useEffect(() => {
    if (me !== undefined && ownerId === '') setOwnerId(me.user.id);
  }, [me, ownerId]);

  /** Defaults that follow from the account: basis, region, segment. */
  useEffect(() => {
    const record = account.data?.account;
    if (record === undefined) return;
    setPriceBasis(record.channel_type === 'distributor' ? 'distributor_resale' : 'menlo_net');
    setRegion(record.region);
    setSegment(record.segment);
  }, [account.data]);

  /** §8 item 6: a controlled account re-asks the question, pre-selected Yes. */
  useEffect(() => {
    if (sensitiveAccount) setAttestation(true);
  }, [sensitiveAccount]);

  /**
   * One selection path for both doors — the picker's own inline create and the
   * `New account` button. A just-created account is in no search result, so it
   * is handed back to the picker as `created`; without that the field holds the
   * id and shows its placeholder.
   */
  const selectAccount = (value: string | null, created?: { id: string; name: string }): void => {
    setAccountId(value);
    if (created !== undefined) setCreatedAccount(created);
  };

  const submitAttestation = async (answer: boolean): Promise<void> => {
    setAttestation(answer);
    if (!answer) return;

    // The refusal is server-side. Sending the answer is what records the
    // `attestation_blocked` event — the primary compliance artifact — and the
    // refusal text comes back with it from `app_settings`.
    setBusy(true);
    try {
      await api('/api/opportunities', {
        method: 'POST',
        body: {
          exportControlledDataWillBeEntered: true,
          ...(accountId === null ? {} : { account_id: accountId }),
        },
      });
      // Unreachable in a correctly configured system: §8 says this returns 403.
      push({ tone: 'error', title: 'The server accepted a blocked attestation.' });
    } catch (error) {
      if (error instanceof ApiError && error.code === 'EXPORT_BLOCKED') {
        setRefusal(error.message);
      } else {
        push({
          tone: 'error',
          title: error instanceof ApiError ? error.message : 'Something went wrong.',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const create = async (): Promise<void> => {
    if (accountId === null) return;
    setBusy(true);
    try {
      const created = await api<{ opportunity: { id: string; opp_number: string } }>(
        '/api/opportunities',
        {
          method: 'POST',
          body: {
            exportControlledDataWillBeEntered: false,
            name: name.trim(),
            account_id: accountId,
            region,
            segment,
            price_basis: priceBasis,
            program_years: programYears,
            ...(isManager && ownerId !== '' ? { owner_user_id: ownerId } : {}),
            ...(account.data?.account.rep_firm_id
              ? { rep_firm_id: account.data.account.rep_firm_id }
              : {}),
            ...(account.data?.account.channel_type
              ? { channel_type: account.data.account.channel_type }
              : {}),
          },
        },
      );

      // The line is a child row, so it is a second call — `opportunity_products`
      // is the source of truth for value and there are no header mirrors (§6.11).
      if (productId !== '' && units !== '' && asp !== '') {
        await api('/api/opportunity-products', {
          method: 'POST',
          body: {
            opportunity_id: created.opportunity.id,
            product_id: productId,
            annual_units: Number(units),
            asp,
            line_type: 'primary',
          },
        });
      }

      push({ tone: 'success', title: `${created.opportunity.opp_number} created` });
      navigate(`/opportunities/${created.opportunity.id}`);
    } catch (error) {
      push({
        tone: 'error',
        title: error instanceof ApiError ? error.message : 'The opportunity was not created.',
      });
    } finally {
      setBusy(false);
    }
  };

  if (refusal !== null) {
    return (
      <div className="max-w-2xl space-y-4">
        <h1 className="text-lg font-semibold">New opportunity</h1>
        <ExportRefusal
          refusalText={refusal}
          onBack={() => {
            setRefusal(null);
            setAttestation(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5">
      <h1 className="text-lg font-semibold">New opportunity</h1>

      <ExportAttestation
        value={attestation}
        onChange={(value) => void submitAttestation(value)}
        disabled={busy}
        {...(sensitiveAccount
          ? {
              preselectedYesReason:
                'This account is flagged export-sensitive by a person, not by its segment',
            }
          : {})}
      />

      {attestation === false && (
        <form
          className="space-y-4 rounded-lg border bg-card p-4"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <p className="text-xs text-muted-foreground">
            The minimum to create a Prospect. Everything else — dates, contacts, more lines — is
            added on the opportunity itself, which is where the stage gates ask for it.
          </p>

          <Field label="Name">
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="What the customer calls this program"
              required
            />
          </Field>

          {/* `htmlFor` rather than the implicit-label shape: the button would
              otherwise sit inside the <label> and every click on it would also
              land on the combobox behind the dialog. */}
          <Field
            label="Account"
            htmlFor="new-opportunity-account"
            hint="Type to search, or create one."
          >
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <AccountPicker
                  id="new-opportunity-account"
                  value={accountId}
                  onChange={selectAccount}
                  created={createdAccount}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreatingAccount(true)}
                disabled={busy}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                New account
              </Button>
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Owner"
              hint={isManager ? undefined : 'You own what you create. Only a manager can assign.'}
            >
              <Select
                value={ownerId}
                disabled={!isManager}
                onChange={(event) => setOwnerId(event.target.value)}
              >
                {reference.users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.display_name}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Price basis"
              hint="Defaults from the account's channel. It decides what every value on this deal means."
            >
              <Select value={priceBasis} onChange={(event) => setPriceBasis(event.target.value)}>
                {PRICE_BASES.map((value) => (
                  <option key={value} value={value}>
                    {humanize(value)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Region">
              <Select value={region} onChange={(event) => setRegion(event.target.value)}>
                {REGIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Segment">
              <Select value={segment} onChange={(event) => setSegment(event.target.value)}>
                {SEGMENTS.map((value) => (
                  <option key={value} value={value}>
                    {humanize(value)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Program life"
              hint="Years the customer's program buys. Lifetime value is annual value × this."
            >
              <Select
                value={String(programYears)}
                onChange={(event) => setProgramYears(Number(event.target.value))}
              >
                {PROGRAM_LIFE_OPTIONS.map((years) => (
                  <option key={years} value={years}>
                    {years} {years === 1 ? 'year' : 'years'}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <fieldset className="rounded-md border p-3">
            <legend className="px-1 text-xs font-medium">First product line</legend>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Part number">
                <Select value={productId} onChange={(event) => setProductId(event.target.value)}>
                  <option value="">—</option>
                  {reference.products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.part_number}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Annual units (EAU)">
                <Input
                  inputMode="numeric"
                  value={units}
                  onChange={(event) => setUnits(event.target.value)}
                />
              </Field>
              <Field label={priceBasis === 'distributor_resale' ? 'Resale ASP' : 'ASP'}>
                <Input
                  inputMode="decimal"
                  value={asp}
                  onChange={(event) => setAsp(event.target.value)}
                />
              </Field>
            </div>
          </fieldset>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate('/opportunities')}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || name.trim() === '' || accountId === null}>
              {busy ? 'Creating…' : 'Create opportunity'}
            </Button>
          </div>
        </form>
      )}

      {/*
        Outside the form on purpose — see the header note. Nothing already
        typed is touched: the dialog is a sibling, so the form is never
        unmounted and no field is reset. Selecting through `selectAccount` is
        what makes the field show the new name rather than its placeholder.
      */}
      {creatingAccount && (
        <CreateAccountDialog
          initialName=""
          onClose={() => setCreatingAccount(false)}
          onCreated={(account) => {
            setCreatingAccount(false);
            selectAccount(account.id, account);
          }}
        />
      )}
    </div>
  );
}
